import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import Grid from '@havenengineering/module-shared-library/dist/components/Grid';

import styles from './aggregated-stepper.module.scss';
import useStepSelections from '../../../../hooks/useStepSelections';
import Icon from '../../widget/icon';
import asset from '../../../../helpers/assets';
import { Query } from '../../../../types/v2/holiday-search';
import { deleteQueryProperties, getFieldsAfterStep, getStep } from '../../../../helpers/redirects';
import { useJourneySteps } from '../../../../hooks/useJourneySteps';
import {
  handleStepperLogin,
  handleStepperNavigation,
  handleStepperNavigationOpen,
} from '../../../../helpers/gtm/gtm-aggregating-journey';

const hasWindow = () => {
  return typeof window === 'object';
};

const BASE_LOGIN_LINK = '/my-account/login-registration/';

type Props = {
  activeStep: string;
  query: Query;
  showLoginButton?: boolean;
  pdp?: boolean;
  singleColumnHeader?: boolean;
};

export const AggregatedStepper = ({
  activeStep,
  query,
  showLoginButton,
  pdp,
  singleColumnHeader = true,
}: Props) => {
  const [isStepperOpen, setIsStepperOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [myAccountLoginLink, setMyAccountLoginLink] = useState(BASE_LOGIN_LINK);

  useEffect(() => {
    const havenID = localStorage.getItem('haven-id-context');
    const loginData = havenID ? JSON.parse(havenID) : {};
    setIsLoggedIn(loginData.detail?.profile);
  }, []);

  useEffect(() => {
    if (hasWindow()) {
      setMyAccountLoginLink(
        `${BASE_LOGIN_LINK}?returnUrl=${encodeURIComponent(window.location.href)}`,
      );
    }
  }, []);

  const { push } = useRouter();

  const journeySteps = useJourneySteps(undefined, query);

  const handleButtonClick = (pathname: string, fromStep: string, toStep: string) => {
    handleStepperNavigation(fromStep, toStep);

    const previousStep = getStep(journeySteps, pathname);
    const fieldsAfterStep = getFieldsAfterStep(journeySteps, previousStep);

    const properties = fieldsAfterStep.map((step) => step?.name);
    const updatedQuery = deleteQueryProperties(query, properties as any, [
      'parkCode',
      'parks',
      'arrivalDate',
      'duration',
      'packageType',
    ]);

    push({
      pathname,
      query: {
        ...updatedQuery,
        referringStep: activeStep.toLowerCase(),
      },
    });
  };

  const handleStepperOpen = (open: boolean) => {
    if (!open) handleStepperNavigationOpen(activeStep.toLowerCase());
    setIsStepperOpen(!open);
  };

  const { stepsWithSelections } = useStepSelections(query);

  const filteredSteps = stepsWithSelections?.filter((step) => step.title);
  const activeStepIndex = filteredSteps?.findIndex((step) => step.name === activeStep);
  const countLabel = `${filteredSteps[activeStepIndex].title}`;
  const buttonLabel = `Step ${activeStepIndex + 1} of ${filteredSteps.length}`;
  const percentage = ((activeStepIndex + 1) / filteredSteps.length) * 100;

  return (
    <>
      <div className={classNames(styles.stepIndicatorRoot, { [styles.pdp]: pdp })}>
        <Grid.Container>
          <Grid.Row justify="center">
            <Grid.Col xs={12} md={12} lg={singleColumnHeader ? 3 : 4}>
              <div className={styles.container}>
                <button
                  type="button"
                  className={classNames(styles.stepCount, { [styles.open]: isStepperOpen })}
                  onClick={() => handleStepperOpen(isStepperOpen)}
                  data-testid="step-count"
                  disabled={activeStepIndex <= 0}
                >
                  {buttonLabel}
                  <Icon
                    className={styles.icon}
                    src={asset('arrow-ios-down-blue.svg')}
                    alt=""
                    role="presentation"
                  />
                </button>
                <ul
                  className={classNames(styles.list, { [styles.open]: isStepperOpen })}
                  data-testid="step-list"
                >
                  {filteredSteps?.map(({ name, title, pathName, selection }, key) => {
                    if (title && key < activeStepIndex)
                      return (
                        <li key={title} className={styles.step}>
                          <button
                            type="button"
                            className={styles.button}
                            onClick={() =>
                              handleButtonClick(
                                pathName,
                                activeStep.toLowerCase(),
                                name ? name.toLowerCase() : '',
                              )
                            }
                          >
                            <span className={styles.label}>{name}</span>
                            {selection && (
                              <span className={styles.selection} title={selection}>
                                {selection}
                              </span>
                            )}
                          </button>
                        </li>
                      );
                    return null;
                  })}
                </ul>
                <div
                  className={classNames(styles.step, styles.active, {
                    [styles.open]: isStepperOpen,
                  })}
                >
                  {countLabel}
                </div>
              </div>
            </Grid.Col>
            <Grid.Col
              xs={12}
              md={12}
              lg={singleColumnHeader ? 3 : 4}
              className={styles.loginButtonContainer}
            >
              {showLoginButton && !isLoggedIn ? (
                <a
                  className={styles.login}
                  href={myAccountLoginLink}
                  onClick={() => handleStepperLogin(activeStep.toLowerCase())}
                >
                  <Icon
                    className={styles.loginIcon}
                    src={asset('stars-outline.svg')}
                    alt=""
                    role="presentation"
                  />
                  <span className={styles.loginLabel}>Log in for tailored results</span>
                </a>
              ) : (
                <div className={styles.loginPlaceholder} data-testid="loginPlaceholder" />
              )}
            </Grid.Col>
          </Grid.Row>
        </Grid.Container>
      </div>

      <div className={styles.progressIndicatorContainer}>
        <div
          className={classNames(styles.progressIndicator, { [styles.full]: percentage === 100 })}
        >
          <div
            className={styles.progress}
            style={{
              width: `${percentage}%`,
            }}
          />
        </div>
      </div>
    </>
  );
};
